<template>
  <div class="game-table-flush">
    <vue-good-table
      styleClass="vgt-table condensed vgt-flush"
      v-bind="tableOptions"
      :columns="columns"
      :rows="rows || []"
      :pagination-options="{ enabled: false }"
      :sort-options="{ enabled: false }"
      :row-click="true"
      @row-click="onRowClick"
      compactMode
    >
      <template #table-row="props">
        <div
          v-if="props.column.field === 'UN'"
          class="d-flex align-items-center gap-2"
        >
          <img
            :src="authorAvatar(props.row)"
            class="forum-author-avatar"
            :alt="props.row.UN"
          >
          <span class="text-sm font-weight-bold text-truncate">{{ props.row.UN }}</span>
        </div>
        <div
          v-else-if="props.column.field === 'Nm'"
          class="d-flex align-items-center gap-2 py-1"
        >
          <span
            class="forum-ctx-badge"
            :class="[ctxClass(props.row.Cx), { 'is-dark': isDarkMode }]"
          >{{ props.row.Cx }}</span>
          <div
            v-if="props.row.Cx === 'Akcie'"
            class="forum-topic-mark"
            :class="{ 'forum-topic-fallback': !showShareLogo(props.row) }"
          >
            <img
              v-if="showShareLogo(props.row)"
              :src="shareLogoUrl(props.row.ISIN)"
              :alt="props.row.Nm"
              @error="onLogoError(props.row.ISIN)"
            >
            <span v-else>{{ shareMonogram(props.row.Nm) }}</span>
          </div>
          <img
            v-else-if="(props.row.Cx === 'Výhra' || props.row.Cx === 'Hra') && rewardSrc(props.row)"
            :src="rewardSrc(props.row)"
            class="forum-reward-img"
            :alt="props.row.Nm"
            role="button"
            @click.stop="openRewardPhoto(props.row)"
          >
          <button
            v-if="props.row.ISINID"
            type="button"
            class="forum-chart-btn"
            :class="{ 'forum-chart-btn-dark': isDarkMode }"
            :title="'Graf: ' + (props.row.Nm || 'akcie')"
            @click.stop="openChart(props.row)"
          >
            <fa-icon icon="fa-solid fa-chart-line" />
          </button>
          <div class="forum-topic-text min-w-0">
            <span class="text-sm font-weight-bold text-truncate d-block">{{ props.row.Nm }}</span>
            <span
              v-if="props.row.ISIN"
              class="forum-isin-code font-monospace"
              :class="isDarkMode ? 'is-dark' : ''"
            >{{ props.row.ISIN }}</span>
          </div>
        </div>
        <span v-else-if="props.column.field === 'Tx'" class="forum-excerpt">
          {{ excerpt(props.row.Tx) }}
        </span>
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>
      <template #emptystate>
        <div class="text-center">
          <span>Zatím žádné příspěvky</span>
        </div>
      </template>
    </vue-good-table>
  </div>
</template>

<script setup>
import { reactive, computed } from 'vue';
import { useStore } from 'vuex';
import { tableOptions } from '../../../../helpers/tableOptions';
import { shareLogoUrl, shareMonogram } from '@/helpers/shareLogos';
import { userAvatarUrl } from '@/helpers/userAvatars';

defineProps({
  rows: Array
});

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const imgRoot = computed(() => process.env.VUE_APP_IMG_ROOT);
const apiRoot = computed(() => process.env.VUE_APP_API_URL);
const logoFailed = reactive({});

const contextPayload = (row) => {
  const payload = { title: row?.Nm || row?.Cx || 'Fórum' };
  if (row?.GameID) payload.GameID = row.GameID;
  else if (row?.ISINID) payload.ISINID = row.ISINID;
  else if (row?.RewardID) payload.RewardID = row.RewardID;
  if (row?.ISIN) payload.ISIN = row.ISIN;
  if (row?.Img) payload.Img = row.Img;
  return payload;
};

const canOpen = (row) => !!(row?.GameID || row?.ISINID || row?.RewardID);

const openRowForum = (row) => {
  if (!canOpen(row)) return;
  store.dispatch('openForum', contextPayload(row));
};

const openChart = (row) => {
  if (!row?.ISINID) return;
  store.dispatch('fetchISINChartData', row);
};

const onRowClick = (params) => openRowForum(params.row);

const excerpt = (text) => {
  const value = String(text || '').replace(/\s+/g, ' ').trim();
  if (value.length <= 140) return value;
  return `${value.slice(0, 140)}…`;
};

const ctxClass = (cx) => {
  if (cx === 'Hra') return 'forum-ctx-game';
  if (cx === 'Akcie') return 'forum-ctx-isin';
  if (cx === 'Výhra') return 'forum-ctx-reward';
  return '';
};

const authorAvatar = (row) => {
  const cache = store.state.imageCacheKey;
  if (row?.UN === 'INGA' || row?.Ug === 'inga.jpg') {
    return `${imgRoot.value}inga.jpg?cache=${cache}`;
  }
  return userAvatarUrl(row?.Ug, cache, true);
};

const onLogoError = (isin) => {
  if (isin) logoFailed[isin] = true;
};

const showShareLogo = (row) => !!(shareLogoUrl(row.ISIN) && !logoFailed[row.ISIN]);

const rewardSrc = (row) => {
  const file = row?.Img;
  if (!file) return '';
  const value = String(file);
  if (value.startsWith('http')) return value;
  if (value.startsWith('img/')) return apiRoot.value + value;
  return imgRoot.value + value;
};

const openRewardPhoto = (row) => {
  const src = rewardSrc(row);
  if (!src) return;
  store.dispatch('openLightbox', { src, alt: row.Nm || 'Výhra' });
};

const columns = reactive([
  {
    label: 'Kdy',
    field: 'Dt',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder',
    tdClass: 'text-sm font-weight-bold text-nowrap',
    width: '130px',
  },
  {
    label: 'Autor',
    field: 'UN',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder',
    tdClass: 'text-sm font-weight-bold',
    width: '150px',
  },
  {
    label: 'Téma',
    field: 'Nm',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder',
    tdClass: 'text-sm font-weight-bold',
  },
  {
    label: 'Příspěvek',
    field: 'Tx',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder',
    tdClass: 'text-sm',
  },
]);
</script>

<style scoped>
.forum-author-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.forum-topic-mark {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.forum-topic-mark img {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.forum-topic-fallback {
  background: rgba(14, 165, 233, 0.16);
  color: #0EA5E9;
  font-size: 0.62rem;
  font-weight: 700;
}

.forum-reward-img {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  cursor: zoom-in;
}

.forum-topic-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.15;
}

.forum-isin-code {
  display: block;
  font-size: 0.68rem;
  font-weight: 600;
  color: #64748B;
  letter-spacing: 0.02em;
}

.forum-isin-code.is-dark {
  color: #94A3B8;
}

.forum-excerpt {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.forum-ctx-badge {
  flex-shrink: 0;
  min-width: 3.85rem;
  text-align: center;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
  box-sizing: border-box;
}

.forum-ctx-game {
  color: #FF6B21;
  background: rgba(255, 107, 33, 0.14);
}

.forum-ctx-isin {
  color: #0EA5E9;
  background: rgba(14, 165, 233, 0.16);
}

.forum-ctx-reward {
  color: #D97706;
  background: rgba(245, 158, 11, 0.2);
}

.forum-ctx-game.is-dark {
  color: #FF8A4C;
  background: rgba(255, 107, 33, 0.2);
}

.forum-ctx-isin.is-dark {
  color: #38BDF8;
  background: rgba(56, 189, 248, 0.18);
}

.forum-ctx-reward.is-dark {
  color: #FBBF24;
  background: rgba(251, 191, 36, 0.18);
}

.forum-chart-btn {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid #7DD3FC;
  background: transparent;
  color: #0EA5E9;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 0.65rem;
}

.forum-chart-btn:hover {
  color: #0284C7;
  border-color: #0EA5E9;
  background: rgba(14, 165, 233, 0.12);
}

.forum-chart-btn-dark {
  border-color: #38BDF8;
  color: #38BDF8;
}

.forum-chart-btn-dark:hover {
  color: #7DD3FC;
  border-color: #7DD3FC;
  background: rgba(56, 189, 248, 0.16);
}
</style>
